import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 99999;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div<{ theme: any }>`
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1rem;
  background: ${p => p.theme.colors.surface};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const TitlePrimary = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.text};
  font-weight: 950;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleSecondary = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 800;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActionBtn = styled.button<{ theme: any }>`
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.text};
  border-radius: 10px;
  padding: 0.45rem 0.7rem;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const FrameWrap = styled.div<{ theme: any }>`
  flex: 1;
  background: ${p => p.theme.colors.background};
  position: relative;
`;

const LoadingCover = styled.div<{ theme: any }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 800;
  z-index: 1;
`;

const Frame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

export function buildClientProfileUrl(params: {
  baseUrl: string;
  clientToken: string;
}): string {
  const base = params.baseUrl.trim();
  if (!base) return '';
  const url = new URL(base, window.location.origin);
  url.searchParams.set('client', params.clientToken);
  return url.toString();
}

export const FullPageIframeModal: React.FC<{
  isOpen: boolean;
  title: string;
  contextTitle?: string;
  url: string;
  theme: any;
  onClose: () => void;
}> = ({ isOpen, title, contextTitle, url, theme, onClose }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) setLoaded(false);
  }, [isOpen]);

  const safeUrl = useMemo(() => (typeof url === 'string' ? url : ''), [url]);
  if (!isOpen) return null;

  if (typeof document === 'undefined') return null;

  const node = (
    <Overlay role="dialog" aria-modal="true" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <TopBar theme={theme} onMouseDown={(e) => e.stopPropagation()}>
        <TitleWrap>
          <TitlePrimary theme={theme}>{contextTitle || title}</TitlePrimary>
          <TitleSecondary theme={theme}>{contextTitle ? title : ''}</TitleSecondary>
        </TitleWrap>
        <Actions>
          <ActionBtn
            theme={theme}
            onClick={() => {
              if (safeUrl) window.open(safeUrl, '_blank', 'noopener,noreferrer');
            }}
            disabled={!safeUrl}
          >
            Open in new tab
          </ActionBtn>
          <ActionBtn theme={theme} onClick={onClose}>Close</ActionBtn>
        </Actions>
      </TopBar>
      <FrameWrap theme={theme} onMouseDown={(e) => e.stopPropagation()}>
        {!loaded && (
          <LoadingCover theme={theme}>Loading...</LoadingCover>
        )}
        <Frame
          title={title}
          src={safeUrl}
          onLoad={() => setLoaded(true)}
        />
      </FrameWrap>
    </Overlay>
  );

  return createPortal(node, document.body);
};

